import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { useCourseDtoActions } from "@ryde/redux";
import {
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  CoursesApi,
  DirectionsServiceEnum,
  errors,
  ICourseDto,
  ICourseMetadata,
  Log,
  normalizeCourseMetadata,
  rebuildRouteLoadingAtom,
  RecountCourseStationsCriteriaDto,
  resolveRecountedStationsInfo,
  SimpleResult,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const useCourseDeepRecalculate = (
  courseDto: Partial<ICourseDto> | undefined,
) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { updateEditingCourseMetadata } = useCourseDtoActions();
  const systemParams = useSystemParams();

  const [isLoading, setIsLoading] = useRecoilState(rebuildRouteLoadingAtom);
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const defaultProvider =
    systemParams.directionsService || DirectionsServiceEnum.Google;

  const handleDeepRecalculate = useEvent(async (): Promise<void> => {
    const stations = courseDto?.metadata?.stationsInfo || [];

    if (!stations.length) {
      Log.warnAndToast(t(errors.course.noStationToRebuild));
      return;
    }

    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const criteriaRecount = new RecountCourseStationsCriteriaDto({
        stations,
        optimize: true,
        provider: defaultProvider,
      });

      const recounted = await CoursesApi.deepRecalculate(criteriaRecount);

      if (!recounted?.stationsInfo) {
        warnAndToastSimpleResult(
          new SimpleResult({
            success: false,
            error: errors.course.stationsNotRecountedSuccessfully,
          }),
          t,
        );
        return;
      }

      const metadataCloned: ICourseMetadata = {
        ...(courseDto?.metadata || {}),
        stationsInfo: resolveRecountedStationsInfo({
          recounted: recounted.stationsInfo,
          source: stations,
          sourceRouteIds: {},
          recountRouteIds: {},
        }),
        [CourseMetadataKeys.routePolyline]: recounted.routePolyline,
        totalDuration: recounted.totalDuration,
        totalDistance: recounted.totalDistance,
        [CourseMetadataKeys.isManualPolylineEdit]: false,
        ...(recounted.directionsService && {
          directionsService: recounted.directionsService,
        }),
      };

      const metadataRecounted = normalizeCourseMetadata({
        metadata: metadataCloned,
        reorderStationsByTime: true,
        timezone,
      });

      const result = await updateEditingCourseMetadata({
        metadata: metadataRecounted,
        optimize: true,
        skipApiCheck: true,
      });

      if (result.success) {
        // The recalculated metadata is applied to the form only; mark it unsaved so Save stays
        // enabled and the unsaved-changes guard protects it until the user persists the course.
        setCourseHasUnsavedStationChange(true);
        Log.infoAndToast(t("general.DEEP_RECALCULATE_SUCCESS"));
      } else {
        warnAndToastSimpleResult(result, t);
      }
    } finally {
      setIsLoading(false);
    }
  });

  return {
    isLoading,
    handleDeepRecalculate,
  };
};
